/* eslint-disable react-hooks/exhaustive-deps */
import {
  checkQuestionaryAccess,
  currentCompanyConfigState,
  getUserEligibleMisureCallback,
  resetContinueFlowStateCallback,
  useBreakpoint,
  currentUserState,
  getUserProfileCallback,
  isStepValid,
} from '@laborability/commons';
import {
  COLORS,
  IconsSize,
  IconsStyle,
  IconSearchComponent,
  LBTButtonIcon,
  IconBurger,
  IconCross,
  LBTImage,
  LBTDialog,
  IconHomeComponent,
  QuestionaryContext,
} from '@laborability/components';
import { Button, Stack } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { APP_ROUTES } from '../../Routes';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { useContext, useEffect, useState } from 'react';

const StyledLogoButton = styled(Button)<{
  datatestid: string;
}>(({ datatestid }) => {
  return {
    datatestid,
    marginLeft: '16px',
  };
});

const StyledStickyHeader = styled(Stack)<{
  backgroundColor: string;
  height: string;
}>`
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 1000;
  border-bottom: 1px solid ${COLORS.getInstance().BLACK};
  ${props => `height: ${props.height};`}
  transition: all 0.5s linear;
  left: 0;
`;
export type LBTNavMenuProps = {
  backgroundColor?: string;
};

const NavMenu: React.FC<LBTNavMenuProps> = ({
  backgroundColor = COLORS.getInstance().WHITE,
}: LBTNavMenuProps) => {
  const { logo, realm } = useRecoilValue(currentCompanyConfigState);
  const currentUser = useRecoilValue(currentUserState);
  const { step, setStep, setIsStepCompleted } = useContext(QuestionaryContext);
  const resetPageState = useRecoilCallback(resetContinueFlowStateCallback, []);
  const fetchMisure = useRecoilCallback(getUserEligibleMisureCallback, []);
  const fetchUser = useRecoilCallback(getUserProfileCallback, []);
  const hasQuestionaryAccess = checkQuestionaryAccess(realm ?? '');
  const location = useLocation();
  const navigate = useNavigate();
  const { isDesktop } = useBreakpoint();
  const [isModalOpen, setIsModalOpen] = useState<string>('');

  const confirmNavigate = (navigateTo: string) => {
    if (location.pathname === `/${APP_ROUTES.QUESTIONARY}` && step)
      setIsModalOpen(navigateTo);
    else navigate(navigateTo);
  };

  const getMessage = () => {
    if (!isStepValid(step, currentUser)) {
      return `Sei sicuro di voler uscire? Poiché hai effettuato delle
         modifiche al questionario, devi ricontrollare tutte le risposte 
         che hai dato precedentemente per assicurarti che siano ancora corrette. 
         Se esci da questionario senza proseguire, potrebbero comparirti alcuni triangoli rossi nel menu > 
         questionario a fianco a ciascun passo: questo significa che devi rivedere le risposte. Per farlo, clicca 
         sul bottone "Continua a rispondere" oppure "Sblocca i tuoi bonus" nella homepage.`;
    }

    return `Le risposte che ci hai dato fin qui sono salvate e puoi vederle dal
        menu. Puoi modificarle da lì in qualsiasi momento.`;
  };

  useEffect(() => {
    if (!!isModalOpen) void fetchUser();
  }, [isModalOpen]);

  return (
    <>
      <StyledStickyHeader
        height={isDesktop ? '100px' : '64px'}
        backgroundColor={backgroundColor}
        direction="column"
      >
        <Stack
          direction="row"
          alignItems="center"
          width="100%"
          height="100%"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center">
            <StyledLogoButton
              datatestid="link_topbar_logo"
              onClick={() => {
                setIsStepCompleted(false);
                confirmNavigate(APP_ROUTES.HOME);
              }}
            >
              {logo ? (
                <img
                  src={logo}
                  style={{
                    height: isDesktop ? '24px' : '20px',
                  }}
                  alt=""
                />
              ) : (
                <LBTImage
                  image="drittoLogo"
                  height={isDesktop ? '24px' : '20px'}
                  width={isDesktop ? '98px' : '82px'}
                />
              )}
            </StyledLogoButton>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            marginRight={'16px'}
          >
            <LBTButtonIcon
              datatestid="link_topbar_search"
              onClick={() => {
                setIsStepCompleted(false);
                confirmNavigate(`${APP_ROUTES.SEARCH_MISURE}`);
              }}
            >
              <IconSearchComponent
                datatestid="link_topbar_search"
                size={IconsSize.LARGE}
                style={IconsStyle.FILLED}
                color={COLORS.getInstance().BLACK}
              />
            </LBTButtonIcon>
            {hasQuestionaryAccess ? (
              <LBTButtonIcon
                datatestid="link_topbar_burger"
                onClick={() => {
                  setIsStepCompleted(false);
                  if (location.pathname === `/${APP_ROUTES.MENU}`)
                    return confirmNavigate(APP_ROUTES.HOME);
                  confirmNavigate(APP_ROUTES.MENU);
                }}
              >
                {location.pathname === `/${APP_ROUTES.MENU}` ? (
                  <IconCross datatestid="link_topbar_burger" />
                ) : (
                  <IconBurger datatestid="link_topbar_burger" />
                )}
              </LBTButtonIcon>
            ) : (
              <LBTButtonIcon
                datatestid="link_topbar_home"
                onClick={() => {
                  return confirmNavigate(APP_ROUTES.HOME);
                }}
              >
                <IconHomeComponent
                  size={IconsSize.LARGE}
                  style={IconsStyle.FILLED}
                  color={COLORS.getInstance().BLACK}
                />
              </LBTButtonIcon>
            )}
          </Stack>
        </Stack>
      </StyledStickyHeader>
      <LBTDialog
        datatestidClose="popup_exit_questionario_x"
        datatestidCancel="popup_exit_questionario_annulla"
        datatestidSubmit="popup_exit_questionario_true"
        open={!!isModalOpen}
        onClose={() => setIsModalOpen('')}
        onCancel={() => setIsModalOpen('')}
        onSubmit={async () => {
          const res = await fetchMisure({ force: true });
          if (res) {
            setStep(0);
            navigate(isModalOpen);
            setIsModalOpen('');
            resetPageState();
          }
        }}
        title="Confermi di voler uscire dal questionario?"
      >
        {getMessage()}
      </LBTDialog>
    </>
  );
};

export default NavMenu;
